import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/movies';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于悟空影视',
  description: '悟空影视 - 高清电影免费在线观看平台。了解悟空影视的品牌故事、核心优势与联系方式。',
  url: 'https://www.wukongmovies.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '悟空影视',
    url: 'https://www.wukongmovies.com',
  },
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '悟空影视',
  url: 'https://www.wukongmovies.com',
  description: '悟空影视创立于2018年，是国内领先的免费高清电影在线播放平台。',
  foundingDate: '2018',
  contactPoint: {
    '@type': 'ContactPoint',
    email: aboutInfo.contact.email,
    contactType: 'customer service',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: aboutInfo.contact.address,
    addressLocality: '北京',
    addressRegion: '朝阳区',
    addressCountry: 'CN',
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '悟空影视是免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的！悟空影视所有电影均免费在线观看，无需注册会员、无需付费订阅，打开即可观看高清电影。',
      },
    },
    {
      '@type': 'Question',
      name: '悟空影视支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '悟空影视完美适配电脑、平板、手机等各类设备，无论您使用Windows、Mac、Android还是iOS系统，都能流畅观影。',
      },
    },
    {
      '@type': 'Question',
      name: '悟空影视的电影资源更新频率如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '悟空影视拥有专业的资源团队，院线新片上映后将尽快更新，同时持续丰富经典影片库，确保资源的新鲜度和多样性。',
      },
    },
    {
      '@type': 'Question',
      name: '在悟空影视观影需要安装插件吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '完全不需要！悟空影视采用HTML5播放器技术，直接在浏览器中播放，无需安装任何插件或播放器，方便快捷。',
      },
    },
  ],
};

export default function About() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            <section className="mb-12 md:mb-16">
              <div className="text-center max-w-3xl mx-auto mb-10">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 font-bold text-3xl md:text-4xl mx-auto mb-5">
                  悟
                </div>
                <h1 className="text-3xl md:text-5xl font-heading font-black text-foreground-950 mb-4">
                  关于悟空影视
                </h1>
                <p className="text-lg md:text-xl text-foreground-500 font-heading">
                  {aboutInfo.tagline}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>{aboutInfo.intro}</p>
                    <p>
                      平台名称来源于中国古典名著《西游记》中的齐天大圣孙悟空。悟空拥有七十二变的神通，
                      正如我们的平台——电影资源丰富多样，无论您想看什么类型的影片，在悟空影视都能找到。
                      从动作片的热血沸腾到喜剧片的捧腹大笑，从科幻片的脑洞大开到爱情片的柔情似水，
                      悟空影视以"七十二变"的姿态满足每一位影迷的观影需求。
                    </p>
                    <p><strong className="text-foreground-950">我们的使命：</strong>{aboutInfo.mission}</p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden border border-background-300/40">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20cinema%20interior%20with%20comfortable%20red%20seats%2C%20large%20projection%20screen%20showing%20colorful%20abstract%20visuals%2C%20warm%20ambient%20lighting%2C%20luxurious%20theater%20atmosphere%2C%20cinematic%20photography%20style%2C%20gold%20and%20crimson%20accents%2C%20professional%20interior%20design&width=800&height=500&seq=about-hero&orientation=landscape"
                    alt="悟空影视品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            <section className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {aboutInfo.features.map((feature, index) => (
                  <div
                    key={index}
                    className="p-5 md:p-6 rounded-xl bg-background-200/70 border border-background-300/40"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500 mb-4">
                      <i className={`text-xl ${
                        index === 0 ? 'ri-database-2-line' :
                        index === 1 ? 'ri-4k-line' :
                        index === 2 ? 'ri-flashlight-line' :
                        'ri-smartphone-line'
                      }`}></i>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {aboutInfo.reputation.map((item, index) => (
                  <div
                    key={index}
                    className="p-5 md:p-6 rounded-xl bg-background-200/70 border border-background-300/40"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(item.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{item.text}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary-500 flex items-center justify-center text-background-50 text-xs font-bold">
                        {item.author.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-foreground-800">{item.author}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  { q: '悟空影视是免费的吗？', a: '是的！悟空影视所有电影均免费在线观看，无需注册会员、无需付费订阅，打开即可观看高清电影。' },
                  { q: '悟空影视支持哪些设备观看？', a: '悟空影视完美适配电脑、平板、手机等各类设备，无论您使用Windows、Mac、Android还是iOS系统，都能流畅观影。' },
                  { q: '悟空影视的电影资源更新频率如何？', a: '悟空影视拥有专业的资源团队，院线新片上映后将尽快更新，同时持续丰富经典影片库，确保资源的新鲜度和多样性。' },
                  { q: '在悟空影视观影需要安装插件吗？', a: '完全不需要！悟空影视采用HTML5播放器技术，直接在浏览器中播放，无需安装任何插件或播放器，方便快捷。' },
                  { q: '悟空影视需要注册账号吗？', a: '不需要！悟空影视无需注册即可观看所有影片，真正做到打开即看，让观影体验简单纯粹。' },
                ].map((faq, index) => (
                  <details
                    key={index}
                    className="group rounded-xl bg-background-200/70 border border-background-300/40 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                      <h3 className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                        {faq.q}
                      </h3>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                联系我们
              </h2>
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-5 rounded-xl bg-background-200/70 border border-background-300/40 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mx-auto mb-3">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 mb-1">电子邮箱</h3>
                    <p className="text-xs text-foreground-600 break-all">{aboutInfo.contact.email}</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-200/70 border border-background-300/40 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500 mx-auto mb-3">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 mb-1">微信</h3>
                    <p className="text-xs text-foreground-600">{aboutInfo.contact.wechat}</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-200/70 border border-background-300/40 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-500/10 text-secondary-500 mx-auto mb-3">
                      <i className="ri-weibo-line text-lg"></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 mb-1">微博</h3>
                    <p className="text-xs text-foreground-600">{aboutInfo.contact.weibo}</p>
                  </div>
                </div>
                <div className="mt-5 p-5 rounded-xl bg-background-200/70 border border-background-300/40 text-center">
                  <h3 className="text-sm font-medium text-foreground-950 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-600">{aboutInfo.contact.address}</p>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}